import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiWaveVoter } from "@/generated/models/ApiWaveVoter";
import Link from "next/link";
import Image from "next/image";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Tooltip } from "react-tooltip";
import type { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { isEthereumAddress, isAutoGeneratedHandle } from "@/helpers/AllowlistToolHelpers";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
interface SingleWaveDropVoterProps {
  readonly voter: ApiWaveVoter;
  readonly position: number;
  readonly creditType: ApiWaveCreditType;
}
export const SingleWaveDropVoter: React.FC<SingleWaveDropVoterProps> = t0 => {
  const $ = _c(56);
  const {
    voter,
    position,
    creditType
  } = t0;
  const hasPositiveVotes = !!voter.positive_votes_summed;
  const hasNegativeVotes = !!voter.negative_votes_summed;
  const handleValue = voter.voter.handle ?? "";
  const shouldLimit = isEthereumAddress(handleValue) || isAutoGeneratedHandle(handleValue);
  let t1;
  if ($[0] !== position) {
    t1 = <span className="tw-text-xs tw-font-medium tw-text-iron-600">{position}</span>;
    $[0] = position;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `/${voter.voter.handle}`;
  let t3;
  if ($[2] !== voter.voter.pfp) {
    t3 = voter.voter.pfp ? <Image src={resolveIpfsUrlSync(voter.voter.pfp)} alt="" width={24} height={24} className="tw-size-7 tw-rounded-md tw-ring-1 tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0 tw-object-contain" /> : <div className="tw-size-7 tw-rounded-md tw-ring-1 tw-ring-white/10 tw-bg-iron-800 tw-flex-shrink-0 tw-object-contain" />;
    $[2] = voter.voter.pfp;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = voter.voter.handle ?? voter.voter.id;
  const t5 = `tw-inline-block${shouldLimit ? " tw-truncate tw-max-w-[8rem]" : ""}`;
  let t6;
  if ($[4] !== voter.voter.handle) {
    t6 = <span className="tw-text-sm tw-font-medium tw-text-iron-50 tw-transition-all tw-duration-300 group-hover:tw-text-iron-300">{voter.voter.handle}</span>;
    $[4] = voter.voter.handle;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t4}>{t7}</UserProfileTooltipWrapper>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t8) {
    t9 = <Link href={t2} className="tw-flex tw-items-center tw-gap-2.5 tw-no-underline tw-group hover:tw-opacity-80 tw-transition-all tw-duration-300 tw-whitespace-nowrap">{t3}{t8}</Link>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t1 || $[17] !== t9) {
    t10 = <div className="tw-flex tw-items-center tw-gap-4">{t1}{t9}</div>;
    $[16] = t1;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = `single-voter-${voter.voter.handle}-${position}`;
  let t12;
  if ($[19] !== hasPositiveVotes) {
    t12 = hasPositiveVotes && <div className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-green" />;
    $[19] = hasPositiveVotes;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== hasNegativeVotes) {
    t13 = hasNegativeVotes && <div className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-red" />;
    $[21] = hasNegativeVotes;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t13) {
    t14 = <div className="tw-flex tw-items-center tw-gap-1" data-tooltip-id={t11}>{t12}{t13}</div>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = `single-voter-${voter.voter.handle}-${position}`;
  let t16;
  if ($[27] !== voter.positive_votes_summed) {
    t16 = formatNumberWithCommas(voter.positive_votes_summed);
    $[27] = voter.positive_votes_summed;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16) {
    t17 = <div className="tw-text-green">+{t16}</div>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== hasNegativeVotes || $[32] !== voter.negative_votes_summed) {
    t18 = hasNegativeVotes && <div className="tw-text-red">-{formatNumberWithCommas(voter.negative_votes_summed)}</div>;
    $[31] = hasNegativeVotes;
    $[32] = voter.negative_votes_summed;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t17 || $[35] !== t18) {
    t19 = <div className="tw-text-sm tw-space-x-1">{t17}{t18}</div>;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t15 || $[38] !== t19) {
    t20 = <Tooltip id={t15} place="top" offset={8} opacity={1} style={TOOLTIP_STYLES}>{t19}</Tooltip>;
    $[37] = t15;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t14 || $[41] !== t20) {
    t21 = <>{t14}{t20}</>;
    $[40] = t14;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== voter.absolute_votes_summed) {
    t22 = formatNumberWithCommas(voter.absolute_votes_summed);
    $[43] = voter.absolute_votes_summed;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t22) {
    t23 = <span className="tw-tabular-nums">{t22}</span>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  const t24 = WAVE_VOTING_LABELS[creditType];
  let t25;
  if ($[47] !== t23 || $[48] !== t24) {
    t25 = <span className="tw-text-xs tw-text-iron-600">{t23} {t24} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t21 || $[51] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-gap-3 tw-whitespace-nowrap">{t21}{t25}</div>;
    $[50] = t21;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t10 || $[54] !== t26) {
    t27 = <div className="tw-p-3 tw-flex tw-items-center tw-justify-between hover:tw-bg-white/[0.02] tw-transition-colors">{t10}{t26}</div>;
    $[53] = t10;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  return t27;
};